import { useCallback, useEffect, useState } from "react";
import { FiCheck, FiX, FiClock, FiPlus, FiTrash2, FiRefreshCw } from "react-icons/fi";
import styles from "./facebook_auto_post.module.scss";

const GRAPH_VERSION = "v25.0";
// Facebook yêu cầu giờ hẹn cách hiện tại tối thiểu ~10 phút, chừa thêm 1 phút cho chắc
const MIN_LEAD_MS = 11 * 60 * 1000;
const CREDS_KEY = "fb_auto_post_credentials";

const pad = (n) => String(n).padStart(2, "0");
const toInputValue = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const isHttpUrl = (s) => /^https?:\/\/\S+$/i.test(s);
const isBlobUrl = (s) => /^blob:/i.test(s);
const parseUrls = (text) => text.split("\n").map((s) => s.trim()).filter(Boolean);
const defaultStart = () => toInputValue(new Date(Date.now() + 30 * 60 * 1000));

const readJson = (key, fallback) => {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch {
        return fallback;
    }
};
const writeJson = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* bỏ qua */ }
};
const removeKey = (key) => {
    try { localStorage.removeItem(key); } catch { /* bỏ qua */ }
};

let rowCounter = 0;
const newRow = (pageId = "", startAt = defaultStart()) => ({
    id: `row_${Date.now()}_${rowCounter++}`,
    pageId,
    token: "",
    files: [],
    urlsText: "",
    startAt,
});

async function graphCall(path, { method = "GET", body, params } = {}) {
    const url = new URL(`https://graph.facebook.com/${GRAPH_VERSION}/${path}`);
    if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    const res = await fetch(url, { method, body });
    const data = await res.json();
    if (data.error) throw new Error(data.error.message);
    return data;
}

// Lấy toàn bộ Page mà User token quản lý (có phân trang), trả về [{ id, name, access_token }]
async function fetchManagedPages(userToken) {
    const pages = [];
    let url = `https://graph.facebook.com/${GRAPH_VERSION}/me/accounts?${new URLSearchParams({
        fields: "id,name,access_token",
        limit: "100",
        access_token: userToken,
    })}`;
    while (url) {
        const res = await fetch(url);
        const data = await res.json();
        if (data.error) throw new Error(data.error.message);
        (data.data || []).forEach(({ id, name, access_token }) => pages.push({ id, name, access_token }));
        url = data.paging?.next || null;
    }
    return pages;
}

// Nếu token nhập vào là User token, đổi sang Page token của từng Page. Nếu không được thì dùng luôn token đó.
async function resolvePageToken(pageId, token) {
    try {
        const data = await graphCall(pageId, { params: { fields: "access_token", access_token: token } });
        if (data.access_token) return data.access_token;
    } catch { /* token có thể đã là Page token */ }
    return token;
}

// Một bài = nhiều ảnh của Page đó. Bước 1 upload từng ảnh, bước 2 tạo bài có đính ảnh.
async function publishPost({ pageId, token, images, caption, when }) {
    const mediaIds = [];
    for (const img of images) {
        const fd = new FormData();
        fd.set("access_token", token);
        if (img.file) fd.set("source", img.file, img.file.name);
        else fd.set("url", img.url);
        fd.set("published", "false");
        if (when) fd.set("temporary", "true");
        const { id } = await graphCall(`${pageId}/photos`, { method: "POST", body: fd });
        mediaIds.push(id);
    }

    const post = new URLSearchParams({ access_token: token, message: caption || "" });
    mediaIds.forEach((id, i) => post.set(`attached_media[${i}]`, JSON.stringify({ media_fbid: id })));
    if (when) {
        post.set("published", "false");
        post.set("scheduled_publish_time", String(Math.floor(when.getTime() / 1000)));
        post.set("unpublished_content_type", "SCHEDULED");
    }
    return graphCall(`${pageId}/feed`, { method: "POST", body: post });
}

const postComment = (postId, token, message) =>
    graphCall(`${postId}/comments`, {
        method: "POST",
        body: new URLSearchParams({ message, access_token: token }),
    });

const FileThumb = ({ file, onRemove }) => {
    const [src, setSrc] = useState("");
    useEffect(() => {
        const url = URL.createObjectURL(file);
        setSrc(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    return (
        <div className={styles.thumb}>
            {src && <img src={src} alt={file.name} className={styles.thumbImg} />}
            <button type="button" className={styles.thumbRemove} onClick={onRemove} aria-label="Xóa ảnh">
                <FiX />
            </button>
        </div>
    );
};

const UrlThumb = ({ url, onRemove }) => (
    <div className={styles.thumb}>
        <img src={url} alt="" className={styles.thumbImg} referrerPolicy="no-referrer" />
        <button type="button" className={styles.thumbRemove} onClick={onRemove} aria-label="Xóa link">
            <FiX />
        </button>
    </div>
);

const FacebookAutoPost = () => {
    const [saved] = useState(() => readJson(CREDS_KEY, null));
    const [hasSaved, setHasSaved] = useState(Boolean(saved));
    const [remember, setRemember] = useState(true);
    const [token, setToken] = useState(saved?.token || "");
    const [mode, setMode] = useState("schedule"); // "schedule" | "now"
    const [caption, setCaption] = useState("");
    const [comment, setComment] = useState("");
    const [requireTexts, setRequireTexts] = useState(Boolean(saved?.requireTexts));
    const [rows, setRows] = useState(() => {
        const ids = saved?.pageIds?.length ? saved.pageIds : [""];
        return ids.map((id) => newRow(id));
    });
    const [results, setResults] = useState({});
    const [formError, setFormError] = useState("");
    const [loading, setLoading] = useState(false);

    // Danh sách Page lấy từ /me/accounts sau khi verify
    const [verifiedPages, setVerifiedPages] = useState([]);
    const [verifying, setVerifying] = useState(false);
    const [verifyMsg, setVerifyMsg] = useState({ type: "", text: "" });

    const isNow = mode === "now";
    const allPagesUsed =
        verifiedPages.length > 0 && rows.filter((r) => r.pageId).length >= verifiedPages.length;

    const verifyToken = useCallback(async (value) => {
        const t = (value || "").trim();
        if (!t) return;
        setVerifying(true);
        setVerifyMsg({ type: "", text: "" });
        try {
            const pages = await fetchManagedPages(t);
            setVerifiedPages(pages);
            // Bỏ các Page đã chọn nhưng không còn nằm trong danh sách của token này
            setRows((prev) =>
                prev.map((r) => (r.pageId && !pages.some((p) => p.id === r.pageId) ? { ...r, pageId: "" } : r))
            );
            setVerifyMsg(
                pages.length > 0
                    ? { type: "ok", text: `Token hợp lệ, tìm thấy ${pages.length} Page.` }
                    : { type: "error", text: "Token hợp lệ nhưng không quản lý Page nào." }
            );
        } catch (e) {
            setVerifiedPages([]);
            setVerifyMsg({ type: "error", text: `Verify lỗi: ${e.message}` });
        } finally {
            setVerifying(false);
        }
    }, []);

    // Có token đã lưu thì tự verify lại để nạp danh sách Page
    useEffect(() => {
        if (saved?.token) verifyToken(saved.token);
    }, [saved, verifyToken]);

    const handleTokenChange = (value) => {
        setToken(value);
        // Token đổi thì danh sách Page cũ không còn đáng tin
        setVerifiedPages([]);
        setVerifyMsg({ type: "", text: "" });
    };

    const updateRow = (id, patch) =>
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const addRow = () => setRows((prev) => [...prev, newRow("", prev[prev.length - 1]?.startAt)]);
    const removeRow = (id) => setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
    const applyTimeToAll = () => setRows((prev) => prev.map((r) => ({ ...r, startAt: prev[0].startAt })));

    const removeFile = (rowId, index) =>
        setRows((prev) =>
            prev.map((r) => (r.id === rowId ? { ...r, files: r.files.filter((_, i) => i !== index) } : r))
        );

    const removeUrl = (rowId, index) =>
        setRows((prev) =>
            prev.map((r) => {
                if (r.id !== rowId) return r;
                const urls = parseUrls(r.urlsText).filter((_, i) => i !== index);
                return { ...r, urlsText: urls.join("\n") };
            })
        );

    const handlePasteImages = (rowId, e) => {
        const imgs = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith("image/"));
        if (imgs.length === 0) return; // không phải ảnh thì để dán chữ như bình thường
        e.preventDefault();
        setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, files: [...r.files, ...imgs] } : r)));
    };

    const validateTexts = () => {
        if (requireTexts) {
            if (!caption.trim()) return "Đang bật chế độ bắt buộc: hãy nhập caption.";
            if (!comment.trim()) return "Đang bật chế độ bắt buộc: hãy nhập comment.";
        }
        return "";
    };

    const validateRow = (r, n) => {
        if (!r.pageId.trim()) return `Page ${n}: chọn một Page (bấm Verify ở token chung để tải danh sách).`;
        if (!(r.token || token).trim()) return `Page ${n}: chưa có token (nhập token chung hoặc token riêng).`;
        const urls = parseUrls(r.urlsText);
        if (urls.some(isBlobUrl)) {
            return `Page ${n}: link blob: không dùng được ngoài tab đã tạo ra nó. Hãy tải ảnh về rồi chọn ở ô "Ảnh từ máy", hoặc sao chép ảnh rồi dán bằng Ctrl+V.`;
        }
        if (urls.some((u) => !isHttpUrl(u))) return `Page ${n}: có dòng không phải link http(s).`;
        if (r.files.length + urls.length === 0) return `Page ${n}: chọn ít nhất một ảnh.`;
        if (!isNow) {
            const t = new Date(r.startAt).getTime();
            if (Number.isNaN(t)) return `Page ${n}: giờ đăng không hợp lệ.`;
            if (t < Date.now() + MIN_LEAD_MS) return `Page ${n}: giờ đăng phải cách hiện tại ít nhất 11 phút.`;
        }
        return "";
    };

    const validate = () => {
        const textErr = validateTexts();
        if (textErr) return textErr;
        const seen = new Set();
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (r.pageId && seen.has(r.pageId)) return `Page ${i + 1}: Page này đã được chọn ở dòng khác.`;
            seen.add(r.pageId);
            const err = validateRow(r, i + 1);
            if (err) return err;
        }
        return "";
    };

    const submitRow = async (row) => {
        const setRes = (patch) =>
            setResults((prev) => ({ ...prev, [row.id]: { ...prev[row.id], ...patch } }));
        setRes({ status: "pending", message: "", note: "" });
        try {
            const pageId = row.pageId.trim();
            const images = [
                ...row.files.map((file) => ({ file })),
                ...parseUrls(row.urlsText).map((url) => ({ url })),
            ];

            // Ưu tiên: token riêng của dòng > Page token từ /me/accounts > đổi từ token chung
            const rowToken = row.token.trim();
            const verified = verifiedPages.find((p) => p.id === pageId);
            const pageToken = rowToken
                ? await resolvePageToken(pageId, rowToken)
                : verified?.access_token || (await resolvePageToken(pageId, token.trim()));

            const when = isNow ? null : new Date(row.startAt);
            const post = await publishPost({ pageId, token: pageToken, images, caption, when });

            let note = "";
            const msg = comment.trim();
            if (msg) {
                try {
                    // Comment ngay, kể cả khi bài mới chỉ được lên lịch
                    await postComment(post.id, pageToken, msg);
                    note = "Đã comment.";
                } catch (e) {
                    note = `${isNow ? "Đã đăng" : "Đã lên lịch"} nhưng comment lỗi: ${e.message}`;
                }
            }
            setRes({ status: "ok", note });
        } catch (e) {
            setRes({ status: "error", message: e.message });
        }
    };

    const handleRetry = async (row) => {
        const idx = rows.findIndex((r) => r.id === row.id);
        const err = validateTexts() || validateRow(row, idx + 1);
        if (err) {
            // Báo lỗi ngay tại dòng đó (ví dụ giờ hẹn đã quá hạn, cần sửa lại giờ)
            setResults((prev) => ({ ...prev, [row.id]: { status: "error", message: err, note: "" } }));
            return;
        }
        await submitRow(row);
    };

    const handleSubmit = async () => {
        const err = validate();
        setFormError(err);
        if (err) return;

        if (remember) {
            writeJson(CREDS_KEY, {
                token: token.trim(),
                pageIds: rows.map((r) => r.pageId.trim()),
                requireTexts,
            });
            setHasSaved(true);
        } else {
            removeKey(CREDS_KEY);
            setHasSaved(false);
        }

        setLoading(true);
        setResults({});

        for (const row of rows) {
            await submitRow(row);
        }

        setLoading(false);
    };

    const handleForget = () => {
        removeKey(CREDS_KEY);
        setHasSaved(false);
        setToken("");
        setVerifiedPages([]);
        setVerifyMsg({ type: "", text: "" });
        setRows([newRow()]);
        setRequireTexts(false);
    };

    const okCount = Object.values(results).filter((r) => r.status === "ok").length;
    const busy = loading || Object.values(results).some((r) => r.status === "pending");
    const failCount = Object.values(results).filter((r) => r.status === "error").length;

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <header className={styles.header}>
                    <h1 className={styles.title}>Đăng ảnh nhiều Fanpage</h1>
                    <p className={styles.lead}>
                        Nhập caption và comment một lần. Mỗi Page có ảnh và giờ đăng riêng, ảnh của Page đó được đăng thành một bài.
                    </p>
                </header>

                <section className={styles.card}>
                    <div className={styles.sharedGrid}>
                        <div className={styles.sharedCol}>
                            <div className={styles.field}>
                                <label className={styles.label} htmlFor="shared-token">Token chung (User token)</label>
                                <div className={styles.tokenRow}>
                                    <input
                                        id="shared-token"
                                        className={styles.input}
                                        type="password"
                                        value={token}
                                        onChange={(e) => handleTokenChange(e.target.value)}
                                        placeholder="User token có quyền pages_show_list"
                                        autoComplete="off"
                                    />
                                    <button
                                        type="button"
                                        className={styles.verifyBtn}
                                        onClick={() => verifyToken(token)}
                                        disabled={verifying || !token.trim()}
                                    >
                                        {verifying ? "Đang verify…" : "Verify"}
                                    </button>
                                </div>
                                {verifyMsg.text && (
                                    <span
                                        className={verifyMsg.type === "error" ? styles.itemError : styles.hint}
                                        role={verifyMsg.type === "error" ? "alert" : "status"}
                                    >
                                        {verifyMsg.text}
                                    </span>
                                )}
                            </div>
                            <label className={styles.checkRow}>
                                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                                <span>Ghi nhớ token và danh sách Page đã chọn trên trình duyệt này</span>
                            </label>
                            <p className={styles.hint}>
                                Dữ liệu được lưu không mã hóa trong trình duyệt, chỉ nên dùng trên máy cá nhân của bạn.{" "}
                                {hasSaved && (
                                    <button type="button" className={styles.clearBtn} onClick={handleForget}>
                                        Xóa dữ liệu đã lưu
                                    </button>
                                )}
                            </p>
                        </div>

                        <label className={styles.field}>
                            <span className={styles.label}>Caption (dùng chung cho mọi Page)</span>
                            <textarea
                                className={`${styles.input} ${styles.textarea}`}
                                value={caption}
                                onChange={(e) => setCaption(e.target.value)}
                                rows={3}
                            />
                        </label>
                        <label className={styles.field}>
                            <span className={styles.label}>Comment dưới bài (dùng chung{requireTexts ? ", bắt buộc" : ", không bắt buộc"})</span>
                            <textarea
                                className={`${styles.input} ${styles.textarea}`}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                rows={2}
                            />
                        </label>

                    </div>

                    <label className={styles.checkRow}>
                        <input
                            type="checkbox"
                            checked={requireTexts}
                            onChange={(e) => setRequireTexts(e.target.checked)}
                        />
                        <span>Bắt buộc có caption và comment mới cho đăng bài</span>
                    </label>

                    <div className={styles.modeSwitch} role="radiogroup" aria-label="Cách đăng">
                        <button
                            type="button"
                            role="radio"
                            aria-checked={!isNow}
                            className={`${styles.modeBtn} ${!isNow ? styles.modeBtnActive : ""}`}
                            onClick={() => setMode("schedule")}
                        >
                            Lên lịch
                        </button>
                        <button
                            type="button"
                            role="radio"
                            aria-checked={isNow}
                            className={`${styles.modeBtn} ${isNow ? styles.modeBtnActive : ""}`}
                            onClick={() => setMode("now")}
                        >
                            Đăng ngay
                        </button>
                    </div>
                </section>

                <div className={styles.rowsGrid}>
                    {rows.map((row, idx) => {
                        const res = results[row.id];
                        const urlCount = parseUrls(row.urlsText).length;
                        // Page đã được chọn ở các dòng khác thì không hiện lại trong dropdown này
                        const usedByOthers = new Set(
                            rows.filter((r) => r.id !== row.id && r.pageId).map((r) => r.pageId)
                        );
                        const options = verifiedPages.filter((p) => !usedByOthers.has(p.id));
                        const currentMissing = row.pageId && !verifiedPages.some((p) => p.id === row.pageId);
                        return (
                            <section
                                key={row.id}
                                className={styles.card}
                                onPaste={(e) => handlePasteImages(row.id, e)}
                            >
                                <div className={styles.rowHead}>
                                    <h2 className={styles.subtitle}>Page {idx + 1}</h2>
                                    {rows.length > 1 && (
                                        <button
                                            type="button"
                                            className={styles.iconBtn}
                                            onClick={() => removeRow(row.id)}
                                            aria-label={`Xóa Page ${idx + 1}`}
                                        >
                                            <FiTrash2 />
                                        </button>
                                    )}
                                </div>

                                <div className={styles.stack}>
                                    <label className={styles.field}>
                                        <span className={styles.label}>Chọn Page</span>
                                        <select
                                            className={`${styles.input} ${styles.select}`}
                                            value={row.pageId}
                                            onChange={(e) => updateRow(row.id, { pageId: e.target.value })}
                                            disabled={verifiedPages.length === 0 && !row.pageId}
                                        >
                                            <option value="">
                                                {verifiedPages.length === 0 ? "Hãy Verify token trước" : "— Chọn Page —"}
                                            </option>
                                            {currentMissing && (
                                                <option value={row.pageId}>{row.pageId} (chưa xác minh)</option>
                                            )}
                                            {options.map((p) => (
                                                <option key={p.id} value={p.id}>
                                                    {p.name} ({p.id})
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className={styles.field}>
                                        <span className={styles.label}>Token riêng (không bắt buộc)</span>
                                        <input
                                            className={styles.input}
                                            type="password"
                                            value={row.token}
                                            onChange={(e) => updateRow(row.id, { token: e.target.value })}
                                            placeholder="Bỏ trống để dùng token của Page đã verify"
                                            autoComplete="off"
                                        />
                                    </label>
                                </div>

                                <div className={styles.field}>
                                    <span className={styles.label}>Ảnh từ máy</span>
                                    <input
                                        className={styles.fileInput}
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={(e) => {
                                            const picked = Array.from(e.target.files || []);
                                            setRows((prev) =>
                                                prev.map((r) => (r.id === row.id ? { ...r, files: [...r.files, ...picked] } : r))
                                            );
                                            e.target.value = ""; // cho phép chọn lại cùng một file
                                        }}
                                        aria-label={`Chọn ảnh từ máy cho Page ${idx + 1}`}
                                    />
                                    <span className={styles.hint}>
                                        {row.files.length > 0
                                            ? `Đã chọn ${row.files.length} ảnh.`
                                            : "Chọn nhiều ảnh, hoặc sao chép ảnh rồi bấm vào thẻ này và nhấn Ctrl+V."}
                                    </span>
                                    {row.files.length > 0 && (
                                        <div className={styles.thumbGrid}>
                                            {row.files.map((file, i) => (
                                                <FileThumb
                                                    key={`${file.name}_${file.size}_${file.lastModified}_${i}`}
                                                    file={file}
                                                    onRemove={() => removeFile(row.id, i)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <label className={styles.field}>
                                    <span className={styles.label}>Hoặc dán link ảnh (mỗi dòng một link)</span>
                                    <textarea
                                        className={`${styles.input} ${styles.textarea}`}
                                        value={row.urlsText}
                                        onChange={(e) => updateRow(row.id, { urlsText: e.target.value })}
                                        rows={2}
                                        placeholder="https://example.com/anh-1.jpg"
                                    />
                                    <span className={styles.hint}>{urlCount} link. Link phải mở được công khai.</span>
                                    {urlCount > 0 && (
                                        <div className={styles.thumbGrid}>
                                            {parseUrls(row.urlsText)
                                                .filter(isHttpUrl)
                                                .map((url, i) => (
                                                    <UrlThumb key={`${url}_${i}`} url={url} onRemove={() => removeUrl(row.id, i)} />
                                                ))}
                                        </div>
                                    )}
                                </label>

                                {!isNow && (
                                    <label className={styles.field}>
                                        <span className={styles.label}>Giờ đăng</span>
                                        <input
                                            className={styles.input}
                                            type="datetime-local"
                                            value={row.startAt}
                                            onChange={(e) => updateRow(row.id, { startAt: e.target.value })}
                                        />
                                        {idx === 0 && rows.length > 1 && (
                                            <span className={styles.hint}>
                                                <button type="button" className={styles.clearBtn} onClick={applyTimeToAll}>
                                                    Dùng giờ này cho tất cả Page
                                                </button>
                                            </span>
                                        )}
                                    </label>
                                )}

                                {res && (
                                    <div className={styles.rowResult}>
                                        <span className={`${styles.badge} ${styles[`badge_${res.status}`]}`}>
                                            {res.status === "ok" && <><FiCheck /> {isNow ? "Đã đăng" : "Đã lên lịch"}</>}
                                            {res.status === "error" && <><FiX /> Lỗi</>}
                                            {res.status === "pending" && <><FiClock /> Đang gửi</>}
                                        </span>
                                        {res.status === "error" && <span className={styles.itemError}>{res.message}</span>}
                                        {res.note && <span className={styles.hint}>{res.note}</span>}
                                        {res.status === "error" && (
                                            <button
                                                type="button"
                                                className={styles.retryBtn}
                                                onClick={() => handleRetry(row)}
                                                disabled={busy}
                                            >
                                                <FiRefreshCw /> Đăng lại Page này
                                            </button>
                                        )}
                                    </div>
                                )}
                            </section>
                        );
                    })}
                </div>

                <button
                    type="button"
                    className={styles.addBtn}
                    onClick={addRow}
                    disabled={allPagesUsed}
                    title={allPagesUsed ? "Đã chọn hết các Page có trong danh sách" : undefined}
                >
                    <FiPlus /> Thêm Page
                </button>

                {formError && <p className={styles.error} role="alert">{formError}</p>}

                <div className={styles.submitRow}>
                    <button className={styles.submit} onClick={handleSubmit} disabled={busy}>
                        {busy
                            ? (isNow ? "Đang đăng…" : "Đang lên lịch…")
                            : `${isNow ? "Đăng ngay" : "Lên lịch"} cho ${rows.length} Page`}
                    </button>
                    {Object.keys(results).length > 0 && !loading && (
                        <span className={styles.summary}>
                            {okCount} thành công{failCount > 0 && `, ${failCount} lỗi`}
                        </span>
                    )}
                </div>

            </div>
        </div>
    );
};

export default FacebookAutoPost;